import { z } from "zod";

/**
 * A listing as the SEARCH response gives it. Deliberately not the detail page.
 *
 * Facebook's search payload carries no coordinates -- verified against a live
 * response, which holds only `location.reverse_geocode = {city, state,
 * city_page{id}}`. `cityId` is that `city_page.id`: a stable identifier, where
 * the name is not. UK names collide (Richmond is a London borough and a North
 * Yorkshire town ~330km apart; Newport is three places), so anything keyed on
 * the display name silently merges towns.
 *
 * We no longer need coordinates to decide distance -- Facebook applies the
 * radius filter itself -- so `cityId` is for display and grouping, not maths.
 */
export const Listing = z.object({
  id: z.string(),
  title: z.string().nullable(),
  priceMinor: z.number().int().nullable(),
  currency: z.string().nullable(),
  city: z.string().nullable(),
  cityId: z.string().nullable(),
  categoryId: z.string().nullable(),
  isSold: z.boolean(),
  imageUrl: z.string().nullable(),
  listedAt: z.coerce.date().nullable(),
});
export type Listing = z.infer<typeof Listing>;

/**
 * A listing matched to an alert.
 *
 * `backfill` marks the one-off history pull done when an alert is created, so
 * the customer sees results immediately rather than an empty screen. Backfill
 * rows must never fire a push: nobody wants 40 notifications on signup.
 */
export const Match = z.object({
  id: z.string().uuid(),
  alertId: z.string().uuid(),
  listingId: z.string(),
  listing: Listing,
  matchedAt: z.coerce.date(),
  backfill: z.boolean(),
  notifiedAt: z.coerce.date().nullable(),
});
export type Match = z.infer<typeof Match>;

/**
 * A title reduced to the words in it, for comparing two postings.
 *
 * Marketplace dropship titles are keyword stuffing, and the stuffing is
 * where they differ: the same sofa came back as "Verona Corner Sofa |
 * Comfortable L Shape De..." and again with a different run of punctuation
 * between the same words. So every run of anything that is not a letter or
 * a digit collapses to one space -- pipes, bullets, emoji, repeated
 * hyphens, double spaces -- and case goes with it.
 *
 * Accents are folded by decomposing and dropping the combining marks rather
 * than by replacing them, because replacing them splits the word: "naive"
 * spelled with a diaeresis would otherwise become two tokens.
 *
 * Word ORDER is kept. Sorting the words would merge titles that only share
 * a vocabulary, and this function's answers decide whether a customer is
 * shown a listing at all.
 */
export function normalisedTitle(title: string | null): string | null {
  if (title === null) return null;
  const folded = title
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
  return folded === "" ? null : folded;
}
